<template>
  <ColumnChart
    ref="chart"
    :unit="unit"
    :scale="scale"
    :title="widgettitle"
    :loading="loading"
    :daterange="daterange"
  />
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import ColumnChart from '../ColumnChart.vue';

export default {
  name: 'OEEDaywise',
  props: {
    title: { type: String, default: '' },
    scale: { type: Number, default: 1 },
    unit: { type: String, default: '' },
    lastdays: { type: Number, default: 7 },
  },
  components: {
    ColumnChart,
  },
  data() {
    return {
      daterange: '',
      widgettitle: '',
      loading: false,
      config: {},
      machinenames: [],
      interval: null,
    };
  },
  computed: {
    ...mapState('cellwiseProductionDashboard', [
      'shifts',
      'machines',
      'serverTime',
      'dataFromJSON',
      'userId',
      'customerId',
      'intervalfetch',
      'currentDate',
    ]),
    ...mapGetters('cellwiseProductionDashboard', ['firstShift']),
    cellname() {
      return this.$route.query.selectedcell;
    },
  },
  watch: {
    '$route.query.selectedcell': {
      async handler() {
        if (!this.dataFromJSON) {
          await this.fetchData();
        } else if (this.dummydata) {
          await this.dummydata(this.lastdays ? -1 : undefined);
        }
      },
    },
    async currentDate() {
      if (!this.dataFromJSON) {
        this.loading = false;
        await this.fetchData();
      }
    },
  },
  async mounted() {
    if (this.dataFromJSON) {
      await this.dummydata();
    } else {
      await this.fetchData();
    }
  },
  beforeDestroy() {
    if (this.interval) clearInterval(this.interval);
  },
  methods: {
    ...mapActions('cellwiseProductionDashboard', [
      'allShifts',
      'getMachines',
      'getReports',
      'getFileData',
    ]),

    parseBusinessDate(yyyymmdd) {
      const dateStr = yyyymmdd.toString();
      const year = parseInt(dateStr.substring(0, 4), 10);
      const month = parseInt(dateStr.substring(4, 6), 10) - 1;
      const day = parseInt(dateStr.substring(6, 8), 10);
      return new Date(year, month, day);
    },

    toYYYYMMDD(dateObj) {
      return parseInt(
        `${dateObj.getFullYear()}${String(dateObj.getMonth() + 1).padStart(2, '0')}${String(dateObj.getDate()).padStart(2, '0')}`,
        10,
      );
    },

    async dummydata(index) {
      try {
        let filename = 'jsondata/oeedaywise.json';
        if (index === -1) {
          filename = 'jsondata/oee15daywise.json';
        }
        const res = await this.getFileData(filename);
        const conf = await this.getFileData('config.json');
        if (conf) this.config = conf;

        if (res) {
          this.machinenames = res.machinenames || [];
          const end = new Date().getTime();
          const start = end - (86400000 * this.lastdays);
          this.setTitle(start, end);
          this.setOptions(res.reportdata);
        }
      } catch (e) {
        this.setZeroState();
      }
    },
    async fetchData() {
      if (this.loading) return;
      this.loading = true;

      try {
        if (!this.currentDate) {
          this.setZeroState();
          return;
        }

        const endDateObj = this.parseBusinessDate(this.currentDate);
        endDateObj.setDate(endDateObj.getDate() - 1);

        const startDateObj = new Date(endDateObj.getTime());
        startDateObj.setDate(startDateObj.getDate() - (this.lastdays - 1));

        this.setTitle(startDateObj.getTime(), endDateObj.getTime());

        const startYYYYMMDD = this.toYYYYMMDD(startDateObj);
        const endYYYYMMDD = this.toYYYYMMDD(endDateObj);

        if (!this.machines || this.machines.length === 0) {
          await this.getMachines();
        }

        let filteredMachines = this.machines || [];
        if (this.cellname) {
          filteredMachines = filteredMachines.filter((m) => m.machinecell === this.cellname);
        }
        this.machinenames = filteredMachines.length > 0
          ? filteredMachines.map((m) => m.machinename).sort()
          : ['-'];
        let activeSite = this.$store.state.user?.activeSite
         || this.$store.state.auth?.user?.site_id;
        if (!activeSite) {
          try {
            const swxUser = JSON.parse(localStorage.getItem('user') || '{}');
            activeSite = swxUser?.site_id || swxUser?.site?.id || swxUser?.customer?.site_id;
          } catch (e) { /* ignore */ }
        }
        if (!activeSite && this.machines?.length > 0) {
          activeSite = this.machines[0].siteId || this.machines[0].siteid;
        }

        if (!activeSite) {
          this.setZeroState();
          this.loading = false;
          return;
        }

        const payload = {
          siteid: activeSite,
          start: startYYYYMMDD,
          end: endYYYYMMDD,
          singledaterange: false,
          all: true,
        };

        const reportName = 'dailymachineperformance';
        const result = await this.getReports({ reportName, payload });

        if (result) {
          const parsedResult = typeof result === 'string' ? JSON.parse(result) : result;
          const finalData = parsedResult.reportData || parsedResult;

          if (finalData && finalData.length > 0) {
            this.setOptions(finalData);
          } else {
            this.setZeroState();
          }
        } else {
          this.setZeroState();
        }
      } catch (e) {
        this.setZeroState();
      } finally {
        this.loading = false;
      }
    },

    setOptions(data) {
      if (!data) return;

      const series = [{ name: 'OEE (%)', data: [] }];

      const safeColor = this.config.colors?.safe || '#21CE99';
      const warningColor = this.config.colors?.warning || '#FFDA6B';
      const dangerColor = this.config.colors?.danger || '#ED5353';
      const greenVal = this.config.greaterthangreen || 75;
      const redVal = this.config.lessthanred || 50;

      this.machinenames.forEach((machine) => {
        let oee = 0;

        data.forEach((d) => {
          if (d.machinename === machine) {
            oee = d.oee || d.OEE || 0;
          }
        });
        const oeeinper = Number.isNaN(oee) ? 0 : Math.round(oee * 100) / 100;
        let color = warningColor; // Default Yellow
        if (oeeinper > greenVal) color = safeColor; // Green
        if (oeeinper < redVal || oeeinper === 0) color = dangerColor; // Red

        series[0].data.push({ color, y: oeeinper });
      });

      const plotOptions = {
        series: {
          borderWidth: 0,
          groupPadding: 0,
          dataLabels: { enabled: true, inside: true },
        },
      };

      if (this.$refs.chart) {
        this.$refs.chart.assignData({
          categories: this.machinenames && this.machinenames.length > 0 ? this.machinenames : ['-'],
          series,
          plotOptions,
        });
      }
    },
    setZeroState() {
      const series = [{ name: 'OEE', data: [] }];
      const categories = this.machinenames && this.machinenames.length > 0
        ? this.machinenames
        : ['-'];

      categories.forEach(() => {
        series[0].data.push({ color: '#ED5353', y: 0 });
      });

      const plotOptions = {
        series: {
          borderWidth: 0,
          groupPadding: 0,
          dataLabels: { enabled: true, inside: true },
        },
      };

      if (this.$refs.chart) {
        this.$refs.chart.assignData({ categories, series, plotOptions });
      }
    },

    setTitle(start, end) {
      this.widgettitle = `Last ${this.lastdays} days (%)`;
      this.daterange = `${this.dateFormat(start)} - ${this.dateFormat(end)}`;
    },

    dateFormat(val) {
      if (!val) return '';
      try {
        const date = new Date(val);
        if (Number.isNaN(date.getTime())) return '';
        const day = new Intl.DateTimeFormat('en', { day: '2-digit' }).format(date);
        const year = new Intl.DateTimeFormat('en', { year: 'numeric' }).format(date);
        const month = new Intl.DateTimeFormat('en', { month: '2-digit' }).format(date);
        return `${day}/${month}/${year}`;
      } catch (e) {
        return '';
      }
    },
  },
};
</script>
